import { useCallback, useState } from "react";
import CloseIcon from "@mui/icons-material/Close";
import {
  WhmDialog,
  WhmDialogContent,
  WhmDialogTitle,
  WhmDialogActions,
  WhmIconButton,
  Theme,
  useWhmSnackbar,
  WhmLoadingButton,
  WhmTypography,
} from "@securitize/reactjs-whm";
import { executeDownload } from "../../utils/files.helpers";
import DownloadImage from "./download-file-image.svg";
import { translationKeys, useTranslation } from "../../contexts/I18nContext";
import { getHoldingsFilePresignedUrl } from "../../data/fundHoldingsRequest";
import useScreenSize from "../../hooks/useScreenSize";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  tokenId: string;
  sourceDate: string | null | undefined;
};

const ModalButtons = ({
  getCsvDownloadLink,
  getPdfDownloadLink,
  loadingType,
}: {
  getPdfDownloadLink: () => void;
  getCsvDownloadLink: () => void;
  loadingType: "pdf" | "csv" | null;
}) => {
  const { translate } = useTranslation();
  return (
    <>
      <WhmLoadingButton
        variant="outlined"
        size="medium"
        color={"secondary"}
        onClick={getPdfDownloadLink}
        loading={loadingType === "pdf"}
        loadingPosition="start"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          ".MuiCircularProgress-root": { display: "flex" },
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          {translate(translationKeys.TEXT_HOLDINGS_DOWNLOAD_PDF)}
        </span>
      </WhmLoadingButton>
      <WhmLoadingButton
        variant="contained"
        size="medium"
        onClick={getCsvDownloadLink}
        loading={loadingType === "csv"}
        loadingPosition="start"
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          ".MuiCircularProgress-root": { display: "flex" },
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
          }}
        >
          {translate(translationKeys.TEXT_HOLDINGS_DOWNLOAD_CSV)}
        </span>
      </WhmLoadingButton>
    </>
  );
};

export default function FundHoldingsDownloadModal(props: Props) {
  const { isOpen, onClose, tokenId, sourceDate } = props;
  const snackbar = useWhmSnackbar();
  const { translate } = useTranslation();
  const { isSmall, isMedium } = useScreenSize();
  const [isLoading, setIsLoading] = useState<"pdf" | "csv" | null>(null);

  const getDownloadLink = useCallback(
    async (type: "pdf" | "csv") => {
      setIsLoading(type);
      try {
        const { url } = await getHoldingsFilePresignedUrl({
          tokenId,
          type,
          sourceDate,
        });
        if (!url) throw new Error("No file available for download");
        executeDownload(url);
      } catch (error) {
        snackbar.show(translate(translationKeys.TEXT_HOLDINGS_DOWNLOAD_ERROR), {
          severity: "warning",
        });
      } finally {
        setIsLoading(null);
      }
    },
    [sourceDate, tokenId, snackbar, translate],
  );

  const ButtonsComponent = () => (
    <ModalButtons
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      getCsvDownloadLink={() => getDownloadLink("csv")}
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      getPdfDownloadLink={() => getDownloadLink("pdf")}
      loadingType={isLoading}
    />
  );

  return (
    <WhmDialog
      open={isOpen}
      onClose={onClose}
      fullScreen={isSmall || isMedium}
      maxWidth={!isSmall && !isMedium ? "sm" : "lg"}
      fullWidth={true}
      aria-labelledby="download-dialog-title"
      aria-describedby="download-dialog-description"
    >
      <WhmDialogTitle
        id="delete-dialog-title"
        variant="h4"
        sx={{ display: "flex", justifyContent: "space-between" }}
      >
        {translate(translationKeys.TEXT_HOLDINGS_DOWNLOAD_MODAL_TITLE)}
        <WhmIconButton
          aria-label="close"
          onClick={onClose}
          size="small"
          sx={(theme: Theme) => ({
            color: theme.palette.common.black,
            borderRadius: "50%",
          })}
        >
          <CloseIcon />
        </WhmIconButton>
      </WhmDialogTitle>
      <WhmDialogContent>
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            gap: "28px",
            textAlign: "center",
            marginBottom: "8px",
          }}
        >
          <img src={DownloadImage} alt="Download" width="124px" />
        </div>
        <WhmTypography
          variant="body1"
          sx={{
            textAlign: "left",
            marginTop: "24px",
            marginBottom: "12px",
          }}
        >
          {translate(translationKeys.TEXT_HOLDINGS_DOWNLOAD_MODAL_TEXT)}
        </WhmTypography>
        <WhmTypography
          variant="body1"
          sx={{
            textAlign: "left",
            marginTop: "12px",
            marginBottom: "24px",
          }}
        >
          {translate(
            translationKeys.TEXT_HOLDINGS_DOWNLOAD_MODAL_TEXT_SECONDARY,
          )}
        </WhmTypography>
        {!isSmall && !isMedium && (
          <div
            style={{
              width: "100%",
              display: "flex",
              flexDirection: "row",
              gap: "8px",
              alignItems: "center",
              justifyContent: "flex-end",
              marginBottom: "8px",
            }}
          >
            <ButtonsComponent />
          </div>
        )}
      </WhmDialogContent>
      {(isSmall || isMedium) && (
        <WhmDialogActions
          sx={{
            padding: 4,
            display: "flex",
            flexDirection: isSmall || isMedium ? "row" : "column",
          }}
        >
          <ButtonsComponent />
        </WhmDialogActions>
      )}
    </WhmDialog>
  );
}
